/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Connect wallet button with Privy integration
 */
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { Button, ButtonProps } from '../ui/Button';

export interface ConnectWalletButtonProps extends Omit<ButtonProps, 'onClick' | 'isLoading'> {
  /**
   * Text to display when not connected
   */
  connectText?: string;

  /**
   * Text to display when connecting
   */
  connectingText?: string;
}

/**
 * Button to connect wallet via Privy
 *
 * Shows "Connect Wallet" when not connected, triggers Privy modal on click.
 * Automatically shows loading state while connecting.
 *
 * Phase 1: Uses connectWallet for read-only access
 * Phase 2: Will use login for authenticated access
 */
export function ConnectWalletButton({
  connectText = 'Connect Wallet',
  connectingText = 'Connecting...',
  variant = 'primary',
  size = 'md',
  ...props
}: ConnectWalletButtonProps) {
  const { ready, connectWallet } = usePrivy();
  const { wallets } = useWallets();

  // Show loading state if Privy isn't ready
  const isLoading = !ready;

  // Don't show the button if already connected
  const isConnected = wallets.length > 0;
  if (isConnected) {
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={connectWallet}
      isLoading={isLoading}
      disabled={!ready}
      {...props}
    >
      {isLoading ? connectingText : connectText}
    </Button>
  );
}
